import { useIntl } from "react-intl";
import { distanceBetween } from "src/utils/distanceBetween";
import { formatDistance } from "src/components/FormattedDistance/FormattedDistance";
import useUser from "src/utils/hooks/useUser";
import messages from "src/domain/HotelsScreen/HotelListCard/HotelListCard.messages";
import type { PartialPlace } from "src/domain/HotelsScreen/HotelsScreen.types";
import { hasUsefulCentre, isCityKind, isPointKind } from "../geoKind";

const MIN_DISTANCES = {
  metric: 250,
  imperial: 244,
};

const DISTANCE_MESSAGES = {
  city: [messages.distanceToCityCentre, messages.lessThanDistanceToCentre],
  area: [messages.distanceToCentre, messages.lessThanDistanceToCentre],
  airport: [messages.distanceToAirport, messages.lessThanDistanceToAirport],
  station: [messages.distanceToStation, messages.lessThanDistanceToStation],
  point: [messages.distanceToPoint, messages.lessThanDistanceToPoint],
} as const;

/**
 * A message showing an AS THE CROW FLIES distance from a point (hotel coordinates) to a place with a kind and shortname.
 */

function getDistanceMessage(
  distance: number,
  distanceSystem: keyof typeof MIN_DISTANCES,
  messageType: keyof typeof DISTANCE_MESSAGES,
  intl: ReturnType<typeof useIntl>,
  placeShortName: string,
) {
  const isCloseEnough = distance <= MIN_DISTANCES[distanceSystem];
  const displayDistance = isCloseEnough
    ? MIN_DISTANCES[distanceSystem]
    : distance;
  const message = DISTANCE_MESSAGES[messageType][isCloseEnough ? 1 : 0];
  const formattedDistance = formatDistance(
    displayDistance,
    distanceSystem,
    intl,
    true,
  );

  return intl.formatMessage(message, {
    distance: formattedDistance,
    place: placeShortName,
  });
}

export function useDistanceToPlace(
  hotel: google.maps.LatLngLiteral,
  place: PartialPlace,
) {
  const intl = useIntl();
  const { distanceSystem } = useUser();

  const messageType = getMessageType(place.kind);
  if (!messageType) return undefined;

  const distance = distanceBetween(hotel, place);

  return getDistanceMessage(
    distance,
    distanceSystem,
    messageType,
    intl,
    place.shortName,
  );
}

export function getMessageType(kind: string) {
  if (hasUsefulCentre(kind)) {
    if (isCityKind(kind)) {
      return "city";
    }
    return "area";
  } else if (isPointKind(kind)) {
    if (kind === "airport" || kind === "station") {
      return kind;
    }
    return "point";
  }
  return undefined;
}
